import React from "react";
import { motion, useTransform } from "framer-motion";
import { graphql, useStaticQuery } from "gatsby";
import { useTranslation } from "react-i18next";
import { createUrl, returnNext } from "@utils/caseHelpers";
import { lang, rawName } from "@utils/helpers";
import tw from "twin.macro";
import Img from "gatsby-image";

import PortableText from "@components/portableText";
import ButtonOval from "@components/buttons/oval";

type CaseNextProps = {
  name: string,
  width: number,
  scroll: any
};

const Wrapper = tw.div`
  ml-30 mb-80
  sm:flex sm:items-center sm:mb-150
  md:mb-100
  xl:mb-180
`;

const ShowOff = tw.div`
  text-sm leading-22 -tracking-021
  lg:mb-25 lg:text-md lg:text-second lg:leading-30 lg:-tracking-027
  xl:ml-120
`;

const CaseName = tw.div`
  mb-25 text-2xl leading-60 -tracking-024
  sm:mb-0
  lg:text-4xl lg:leading-60 lg:-tracking-028
  xl:ml-120
`;

const CaseLink = tw(motion.div)`relative z-10 sm:ml-20 lg:ml-40`;

const ImageWrapper = tw(motion.div)`relative h-120`;

const ImageIllustration = tw(Img)`
  absolute! -top-170 left-40 w-280 z-1
  sm:top-0 sm:-left-150
  md:-top-50 md:-left-50
  xl:-top-90 xl:-left-90 xl:w-380
`;

const ShortDesc = tw(motion.div)`
  flex max-w-220 text-sm text-second leading-22 -tracking-021
  sm:ml-auto
  lg:relative lg:max-w-430 lg:right-40 lg:text-md lg:leading-30 lg:-tracking-027
`;

const ShortDescCharacter = tw.div`
  font-pangramBold min-w-20 -mt-4 leading-32
  lg:min-w-40 lg:mr-10 lg:leading-30
`;

const CaseNext = ({ width, scroll, name }: CaseNextProps) => {
  const { t } = useTranslation();

  const { allSanityCases, illustration } = useStaticQuery(graphql`
    query {
      allSanityCases {
        nodes { 
          slug { current },
          order, 
          title 
        }
      }
      illustration: file(relativePath: { eq: "illustration.png" }) {
        childImageSharp {
          fluid(maxWidth: 500) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  `);
  const data = allSanityCases.nodes.find(item => item.slug.current === name);
  const next = returnNext(data.order);
  const nextUrl = createUrl(next.slug.current, lang);

  const Ref = React.useRef();

  const [sectionStart, setSectionStart] = React.useState(0);

  const opacity = useTransform(scroll, [sectionStart + 80, sectionStart + 420], [0, 1]);
  const left = useTransform(scroll, [sectionStart + 80, sectionStart + 420], [-120, 0]);
  const right = useTransform(scroll, [sectionStart + 80, sectionStart + 420], [-120, 0]);

  const imageTop = useTransform(scroll, [sectionStart + 80, sectionStart + 420], [200, 0]);
  const imageLeft = useTransform(scroll, [sectionStart + 80, sectionStart + 420], [-120, 0]);
  const imageOpacity = useTransform(scroll, [sectionStart + 80, sectionStart + 280], [0, 1]);

  React.useLayoutEffect(() => {
    if (!Ref.current) return;

    const offsetStart = window && Ref.current.getClientRects()[0].top + window.pageYOffset - window.innerHeight;

    setSectionStart(offsetStart);
  });

  return (
    <Wrapper ref={Ref}>
      <motion.div className="lg:relative" style={ width >= 1024 && { left, opacity }}>
        <ShowOff>
          {t('Show off1')}
          <br/>
          {t('Show off2')}
        </ShowOff>
        <CaseName>
          { next.title }
        </CaseName>
      </motion.div>
      <CaseLink style={ width >= 1024 && { opacity }}>
        <ButtonOval href={nextUrl} type='noIntl' />
      </CaseLink>
      <ImageWrapper style={ width >= 1024 && { top: imageTop, left: imageLeft, opacity: imageOpacity }}>
        <ImageIllustration fluid={illustration.childImageSharp.fluid} />
      </ImageWrapper>
      <ShortDesc style={ width >= 1024 && { right, opacity }}>
        <ShortDescCharacter>
          {t('Description Character')}
        </ShortDescCharacter>
        <PortableText blocks={next.description[rawName]} />
      </ShortDesc>
    </Wrapper>
  );
};

export default CaseNext;
